import React, { useEffect, useRef, useState } from 'react';
import HOCWithDisplayName from '../HOCWithDisplayName';

const {
  __slab: { CSP_NONCE },
} = window;
const urlLoaded: Record<string, boolean> = {};

type Options = { dataset?: DOMStringMap };

const useScript = (url: string, { dataset }: Options = {}) => {
  const scriptRef = useRef<HTMLScriptElement>();
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const [loaded, setLoaded] = useState(urlLoaded[url]);

  useEffect(() => {
    if (loaded) {
      return undefined;
    }
    const script = document.createElement('script');
    script.src = url;
    script.nonce = CSP_NONCE;
    script.async = true;
    Object.assign(script.dataset, dataset || {});

    script.onload = () => {
      urlLoaded[url] = true;
      setLoaded(true);
    };

    scriptRef.current = script;

    document.body.appendChild(script);

    return () => {
      if (scriptRef.current) {
        scriptRef.current.onload = null;
      }
    };
  }, [url, dataset, loaded]);

  return { loaded };
};

export const withScript =
  (url: string, options?: Options) =>
  <P extends object>(Component: React.ComponentType<P>) => {
    const WrappedComponent = (props: P) => {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
      const { loaded } = useScript(url, options);

      return loaded ? <Component {...props} /> : null;
    };

    return HOCWithDisplayName('withScript')(WrappedComponent);
  };

export default useScript;
